import { useState } from 'react'
import { createPortal } from 'react-dom'

import { api, ApiError } from '../../api/client'
import { useEscape } from '../../hooks/useEscape'
import { useTexte } from '../../texte'
import { Blattkopf } from '../../components/Blattkopf'

/**
 * Einen Ort nach seinem Namen finden — „Nidarosdom", „Bergen".
 *
 * **Das ist ein freies Suchfeld, und CLAUDE.md schloss genau das aus.** Zur
 * Umgebungssuche steht dort: „hinaus geht eine von sieben festen Marken —
 * kein frei eingetippter Begriff." Hier ist es einer, und die Regel ist dafür
 * nachgezogen worden statt umgangen — in CLAUDE.md und in
 * `docs/datenschutz-leser.md`.
 *
 * Was die Sache trägt, ist dasselbe wie bei der Umgebungssuche: **nur der
 * Autor** löst sie aus, **der Server** fragt (hinaus gehen seine IP und
 * der Begriff, nie die eines Lesers), und **auf Knopfdruck**, nicht beim
 * Anzeigen.
 *
 * **Das Blatt hängt am Seitenkörper, und zwar hier und nicht beim Aufrufer**
 * (1.63.0). `backdrop-filter` macht ein Element zum Bezugsrahmen für
 * `position: fixed`, und jedes Blatt trägt es seit #8 -- eine Ortssuche, die
 * im DOM in einem anderen Blatt steckt, klebt damit an jenem Blatt und
 * scrollt mit. Genau so stand sie im Wetter-Blatt.
 *
 * **Behoben war das schon, an der falschen Stelle:** `Wetterwahl` rief
 * `createPortal` selbst, `KartePage` nicht -- und kam damit durch, weil über
 * ihm kein Bezugsrahmen liegt. Eine Regel an zwei Stellen läuft auseinander,
 * und wer die Suche ein drittes Mal aufruft, müsste es wissen. Deshalb steht
 * der Griff jetzt im Bauteil.
 *
 * **Dasselbe gilt für das Schließen.** Escape ging aus dem Wetter-Blatt und
 * von der Karte nicht, weil dort die Elternkomponente zuhörte und hier
 * niemand; der Schleier nahm gar keinen Tipp an, obwohl der Kommentar an
 * `.ortssuche` das behauptete. Beides hängt jetzt am Bauteil.
 */
export interface Ortstreffer {
  name: string
  lat: number
  lon: number
  gegend: string
}

export function Ortssuche({
  tripId,
  onGewaehlt,
  onZu,
}: {
  tripId: number
  onGewaehlt: (treffer: Ortstreffer) => void
  onZu: () => void
}) {
  const texte = useTexte()
  const t = texte.karte.ortssuche
  const [wort, setWort] = useState('')
  const [treffer, setTreffer] = useState<Ortstreffer[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [befund, setBefund] = useState<string | null>(null)

  /*
   * **Zwei Zuhörer sind hier kein Streit.** Aus dem Wetter-Blatt heraus hören
   * jetzt beide -- `Wetterwahl` und diese Suche --, und beide tun dasselbe:
   * die Suche zugehen lassen. `useEscape` hängt an `window`, da steigt nichts
   * auf; eine Sperre wäre also nichts, was etwas hält.
   */
  useEscape(onZu)

  async function suche() {
    const begriff = wort.trim()
    if (begriff === '') return
    setLaeuft(true)
    setProblem(null)
    try {
      const antwort = await api.post<{
        results: Ortstreffer[]
        status: number
        diagnose?: string
      }>(`/api/trips/${tripId}/ortssuche`, { q: begriff })
      setTreffer(antwort.results)
      setBefund(antwort.diagnose ?? null)
      if (antwort.results.length === 0) {
        // **Kein stilles Nichts.** „Keine Treffer" und „der Dienst hat
        // geschwiegen" sehen sonst gleich aus — derselbe Befund wie bei der
        // Umgebungssuche, und dieselbe Lehre.
        setProblem(
          antwort.status === 200 ? t.nichts : t.keineAntwort(antwort.status),
        )
      }
    } catch (fehler) {
      setTreffer([])
      setBefund(null)
      setProblem(
        fehler instanceof ApiError && fehler.offline
          ? t.offline
          : fehler instanceof Error
            ? fehler.message
            : texte.bausteine.suche.fehlgeschlagen,
      )
    } finally {
      setLaeuft(false)
    }
  }

  return createPortal(
    <div
      className="ortssuche"
      role="dialog"
      aria-modal="true"
      aria-label={t.titel}
      /* Der Schleier schließt -- das `stopPropagation` am Blatt darunter
         stand schon da und bekommt damit seinen Sinn. */
      onClick={onZu}
    >
      <div className="bogen__blatt bogen__blatt--gegliedert ortssuche__blatt" onClick={(e) => e.stopPropagation()}>
        <Blattkopf titel={t.titel} onSchliessen={onZu} schliessenLabel={t.schliessen} />

        {/* **Kopf, Inhalt und Fuß als Geschwister** (1.61.0). Here the gain is
            the plainest of the round: today the **search field** scrolls away
            as soon as hits stand below it. Now only this box scrolls. */}
        <div className="bogen__rolle">
          <form
            className="ortssuche__zeile"
            onSubmit={(event) => {
              event.preventDefault()
              void suche()
            }}
          >
            <input
              className="ortssuche__feld"
              type="search"
              value={wort}
              autoFocus
              enterKeyHint="search"
              placeholder={texte.bausteine.suche.platzhalter}
              aria-label={t.feld}
              onChange={(event) => setWort(event.target.value)}
            />
            <button type="submit" className="button button--small" disabled={laeuft}>
              {laeuft ? t.laeuft : t.suchen}
            </button>
          </form>

          {treffer !== null && treffer.length > 0 && (
            <ul className="ortssuche__liste">
              {treffer.map((ort, i) => (
                <li key={`${ort.lat},${ort.lon},${i}`}>
                  <button
                    type="button"
                    className="ortssuche__treffer"
                    onClick={() => {
                      onGewaehlt(ort)
                      onZu()
                    }}
                  >
                    <span className="ortssuche__name">{ort.name}</span>
                    {ort.gegend !== '' && <span className="ortssuche__gegend">{ort.gegend}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {problem !== null && (
            <p className="feld__hinweis" role="alert">
              {problem}
            </p>
          )}

          {/* **Die Befundzeile — zum Suchen, nicht zum Ansehen.**

              Aus dem Betrieb: „Graz" antwortete mit *„Der Server hat einen
              Fehler gemeldet"*, und mehr stand nirgends. Der Satz war wahr und
              trotzdem wertlos. Hier steht jetzt, was gefragt wurde und was
              zurückkam — dieselbe Zeile wie beim Videobefund und bei der
              Umgebungssuche. */}
          {befund !== null && befund !== '' && problem !== null && (
            <p className="bogen__befund">{befund}</p>
          )}

          {/* **Was hinausgeht, steht dabei.** Wer tippt, soll wissen, wohin es
              geht — dasselbe Prinzip wie die GIPHY-Nennung in der GIF-Auswahl. */}
          <p className="ortssuche__quelle">
            {t.quelleVor}
            <strong>{t.quelleName}</strong>
            {t.quelleNach}
          </p>
        </div>
      </div>
    </div>,
    document.body,
  )
}
